// managers/pdar/PdarsRenderer.ts
import type { Map as MapType } from "maplibre-gl";
import type { PdarPlatform, PdarTargets, Pdars } from "../../types/pdar";
import type { PdarMarkerType } from "../../markers/pdar";

export class PdarsRenderer {
  private markerCache = new Set<string>();

  constructor(private map: MapType) {}

  /**
   * Resolves the pre-registered sprite key for the Platform asset.
   */
  public getPlatformSpriteKey(
    platform: PdarPlatform,
    isSelected: boolean = false,
  ): string {
    const intent = "friendly"; // Base platforms default to friendly context
    const baseKey = `${intent}-pdar-platform`;
    return this.resolveCachedMarkerId(baseKey, isSelected);
  }

  /**
   * Resolves the pre-registered sprite key for individual tracking Sensors.
   */
  public getSensorSpriteKey(
    station: Pdars,
    isSelected: boolean = false,
  ): string {
    // Maps operational health states onto corresponding intent/color contexts
    let intent = "friendly";
    if (
      station.health_state === "critical" ||
      station.health_state === "offline"
    ) {
      intent = "hostile"; // Triggers warning tints via WebGL
    } else if (station.health_state === "warning") {
      intent = "neutral";
    }

    const baseKey = `${intent}-pdar-sensor`;
    return this.resolveCachedMarkerId(baseKey, isSelected);
  }

  /**
   * Resolves the pre-registered sprite key for moving Targets.
   */
  public getTargetSpriteKey(
    target: PdarTargets,
    isSelected: boolean = false,
  ): string {
    const intent = target.intent || "unknown";
    const baseKey = `${intent}-pdar-target`;
    return this.resolveCachedMarkerId(baseKey, isSelected);
  }

  /**
   * Internal routing function to trace selection overrides and append to texture tracking maps
   */
  private resolveCachedMarkerId(
    baseSpriteKey: string,
    isSelected: boolean,
  ): string {
    // If selected, we append a specialized modifier handled by layer styling,
    // or return the primary base sprite mapping key registered in our pipeline.
    const markerId = isSelected ? `${baseSpriteKey}-selected` : baseSpriteKey;

    // Maintain internal cache ledger for consistency
    if (!this.markerCache.has(markerId)) {
      this.markerCache.add(markerId);
    }

    return markerId;
  }

  public hasMarker(markerId: string): boolean {
    return this.markerCache.has(markerId);
  }

  public clearCache() {
    this.markerCache.clear();
  }

  public destroy() {
    this.clearCache();
  }
}
